/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `CompanyCatalog` for the Caintra Connect frontend.
 * @module pages/CompanyCatalog
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import styles from "../styles/CompanyCatalog.module.css";

import LanguageSelector from "../components/LanguageSelector/index.jsx";
import NotificationBell from "../components/NotificationBell/index.jsx";
import SearchBar from "../components/SearchBar/index.jsx";
import PageSelector from "../components/PageSelector/index.jsx";
import ResultsDropdown from "../components/ResultsDropdown/index.jsx";

import { calculateProgressStep } from "../logic/companyEdit/index.js";
import { PERCENTAGE_STEP } from "../logic/companyCatalog/index.js";

import { CircularProgress, Backdrop, Switch } from "@mui/material";

import { ReactComponent as TitleIcon } from "../assets/icons/CompanyCatalogPage/TitleIcon.svg";
import { ReactComponent as ActiveCompanyIcon } from "../assets/icons/CompanyCatalogPage/ActiveCompanyIcon.svg";
import { ReactComponent as InactiveCompanyIcon } from "../assets/icons/CompanyCatalogPage/InactiveCompanyIcon.svg";
import { ReactComponent as VisibilityIcon } from "../assets/icons/CompanyCatalogPage/VisibilityIcon.svg";
import { ReactComponent as EditIcon } from "../assets/icons/CompanyCatalogPage/EditIcon.svg";

import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { canAccessCompanyCatalog } from "../utils/accessControl";
import useRequireAccess from "../hooks/useRequireAccess";
import useCompanyCatalog from "../hooks/useCompanyCatalog";

import { getCompanySizeOptions } from "../data/selectOptions";
import { getSelectedOption } from "../utils/common.js";

/**
 * Company Catalog page. Displays a sortable, searchable table of companies
 * with size, commercial name, profile completion percentage, primary contact,
 * optional "create requirements" toggle (for collaborators), and actions
 * (view/edit). Supports pagination, CSV export of the current table dataset,
 * results-per-page dropdown, and loading backdrop. Collaborators can toggle
 * create-requirements permission per company.
 * @returns {JSX.Element}
 */
const CompanyCatalog = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { allowed } = useRequireAccess(canAccessCompanyCatalog);
  const {
    searchQuery,
    loading,
    togglingCompanyId,
    showCreateRequirementsColumn,
    paginatedCompaniesList,
    totalPages,
    currentPage,
    limitReached,
    companiesData,
    handleSort,
    updateSearchQuery,
    handleSearch,
    handleExportCsv,
    handleResultsLimitChange,
    handlePageChange,
    handleCreateRequirementsChange,
    handleEditCompanyClick,
  } = useCompanyCatalog();

  const companySizeOptions = getCompanySizeOptions(t);

  if (!allowed) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.languages}>
          <NotificationBell navigate={navigate} />
          <LanguageSelector />
        </div>

        <div className={styles.headerInfoContainer}>
          <div className={styles.headerInfo}>
            <TitleIcon />
            <h1 className={styles.title}>{t("pages.companyCatalog.header")}</h1>
          </div>
          <div className={styles.toolbar}>
            <SearchBar
              placeholder={t("pages.companyCatalog.searchBarPlaceholder")}
              value={searchQuery}
              onChange={updateSearchQuery}
              onSearch={handleSearch}
              className={styles.searchBarWrapper}
              width="100%"
            />
            <button
              type="button"
              className={styles.exportButton}
              onClick={handleExportCsv}
              disabled={loading || companiesData.length === 0}
            >
              {t("pages.companyCatalog.exportButton")}
            </button>
          </div>
        </div>
      </div>

      <div className={styles.companiesTableContainer}>
        <table className={styles.companiesTable}>
          <thead>
            <tr>
              <th onClick={handleSort}>
                {t("pages.companyCatalog.sizeHeader")}
              </th>
              <th>{t("pages.companyCatalog.commercialNameHeader")}</th>
              <th>{t("pages.companyCatalog.percentageHeader")}</th>
              <th>{t("pages.companyCatalog.primaryContactHeader")}</th>
              {showCreateRequirementsColumn && (
                <th>{t("pages.companyCatalog.createRequirementsHeader")}</th>
              )}
              <th>{t("pages.companyCatalog.actionsHeader")}</th>
            </tr>
          </thead>
          <tbody>
            {paginatedCompaniesList.map((company) => {
              const companySizeSelectedOption = getSelectedOption(
                companySizeOptions,
                company?.tamanoDeLaEmpresa,
              );
              const companySizeLabel = companySizeSelectedOption?.label || "";

              const progressPercentage = (
                calculateProgressStep(company) * PERCENTAGE_STEP
              ).toString();

              return (
                <tr
                  key={company._id}
                  className={!company?.activo ? styles.inactiveRow : ""}
                >
                  <td className={styles.companySize}>
                    <span className={styles.companySizeBadge}>
                      {companySizeLabel}
                    </span>
                  </td>
                  <td className={styles.companyNameCell}>
                    <div className={styles.companyNameContainer}>
                      {company?.activo ? (
                        <ActiveCompanyIcon
                          className={styles.companyStatusIcon}
                        />
                      ) : (
                        <InactiveCompanyIcon
                          className={styles.companyStatusIcon}
                        />
                      )}
                      <div className={styles.companyInfoContainer}>
                        <span className={styles.companyName}>
                          {company?.nombreComercial}
                        </span>
                        <span className={styles.companyDetails}>
                          {company?.numeroSocio && (
                            <span>{company?.numeroSocio}</span>
                          )}
                          {company?.numeroSocio && company?.RFC && (
                            <span className={styles.companySeparator}>/</span>
                          )}
                          {company?.RFC && <span>{company?.RFC}</span>}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className={styles.companyPercentage}>
                    {progressPercentage}%
                  </td>
                  <td className={styles.companyPrimaryContact}>
                    {company?.correoElectronicoPrincipal}
                  </td>
                  {showCreateRequirementsColumn && (
                    <td className={styles.createRequirementsCell}>
                      <Switch
                        data-company-id={company._id}
                        checked={
                          (company?.crearRequerimientos ??
                            company?.createRequirementsPermissions) !== false
                        }
                        disabled={togglingCompanyId === company._id}
                        onChange={handleCreateRequirementsChange}
                        color="primary"
                      />
                    </td>
                  )}
                  <td>
                    <VisibilityIcon className={styles.visibilityIcon} />
                    <EditIcon
                      className={styles.editIcon}
                      data-company-id={company._id}
                      onClick={handleEditCompanyClick}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className={styles.footer}>
        <ResultsDropdown onResultsLimitChange={handleResultsLimitChange} />

        <PageSelector
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          disabled={{ next: limitReached }}
        />
      </div>

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        }}
        open={loading}
      >
        <CircularProgress
          size={60}
          thickness={4}
          sx={{
            color: "#4eabe7",
          }}
        />
      </Backdrop>
    </div>
  );
};

export default CompanyCatalog;
